import { createContext, useContext, useEffect, useMemo, useState, useCallback } from "react";
import { toast } from "sonner";
import api from "../lib/api";

const AppCtx = createContext(null);

export const AppProvider = ({ children }) => {
  const [customer, setCustomer] = useState(null);
  const [loading, setLoading] = useState(true);

  const loadMe = useCallback(async () => {
    const token = localStorage.getItem("dtile_buyer_token");
    if (!token) { setCustomer(null); setLoading(false); return; }
    try {
      const { data } = await api.get("/auth/buyer/me");
      setCustomer(data);
    } catch {
      localStorage.removeItem("dtile_buyer_token");
      setCustomer(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadMe(); }, [loadMe]);

  const loginBuyer = useCallback(async ({ mobile, password }) => {
    const { data } = await api.post("/auth/buyer/login", { mobile, password });
    localStorage.setItem("dtile_buyer_token", data.token);
    setCustomer(data.buyer);
    return data.buyer;
  }, []);

  const signupBuyer = useCallback(async (payload) => {
    const { data } = await api.post("/auth/buyer/signup", payload);
    return data;
  }, []);

  const patchCustomer = useCallback(async (update) => {
    if (!customer) return;
    const { data } = await api.patch(`/customers/${customer.id}`, update);
    setCustomer(data);
    return data;
  }, [customer]);

  const logout = useCallback(() => {
    localStorage.removeItem("dtile_buyer_token");
    setCustomer(null);
  }, []);

  // Auto-logout after 60 minutes of inactivity — parity with admin panel.
  useEffect(() => {
    if (!customer) return;
    const IDLE_MS = 60 * 60 * 1000; // 1 hour
    let timer;
    const reset = () => {
      clearTimeout(timer);
      timer = setTimeout(() => {
        toast.info("Session expired · signed out for security");
        logout();
      }, IDLE_MS);
    };
    const events = ["mousemove", "keydown", "click", "scroll", "touchstart"];
    events.forEach(e => window.addEventListener(e, reset, { passive: true }));
    reset();
    return () => {
      clearTimeout(timer);
      events.forEach(e => window.removeEventListener(e, reset));
    };
  }, [customer, logout]);

  const value = useMemo(() => ({
    customer, setCustomer, loading,
    loginBuyer, signupBuyer, patchCustomer, logout,
  }), [customer, loading, loginBuyer, signupBuyer, patchCustomer, logout]);

  return (
    <AppCtx.Provider value={value}>
      {children}
    </AppCtx.Provider>
  );
};

export const useApp = () => {
  const ctx = useContext(AppCtx);
  if (!ctx) throw new Error("useApp must be used within AppProvider");
  return ctx;
};
